import * as React from 'react'
import {
  DateField as AriaDateField,
  DateInput as AriaDateInput,
  DateSegment as AriaDateSegment,
  type DateFieldProps as AriaDateFieldProps,
  type DateValue,
} from 'react-aria-components'
import { cn } from '../../lib/utils'

/**
 * DateField — a segmented date input (day · month · year) built on
 * react-aria-components. Each segment is independently editable with the
 * keyboard (type a number, arrow up/down to step), which is faster and more
 * accessible than a free-text date string.
 *
 *   <DateField aria-label="Appointment" defaultValue={today(getLocalTimeZone())} />
 *
 * This is the DS's first react-aria component (the rest are Radix). The input
 * box mirrors our `Input`/`InputGroup` look — gray-300 border, basil focus ring,
 * the same `sm | md | lg` heights — and the focused segment gets a basil fill.
 * `TimeField` and `DatePicker` reuse the exported `DateInput`/`DateSegment`.
 *
 * Boolean props follow our DS naming (`disabled`, `readOnly`, `required`,
 * `invalid`) and are mapped to react-aria's `is*` equivalents.
 */

type FieldSize = 'sm' | 'md' | 'lg'

/** Shared input-box sizing — matches Input/InputGroup heights + text. */
const inputSize: Record<FieldSize, string> = {
  sm: 'h-8 px-2.5 text-xs',
  md: 'h-9 px-3 text-sm',
  lg: 'h-10 px-3.5 text-base',
}

/** The bordered segment container (the "input" look). */
const dateInputBase =
  'inline-flex w-fit items-center rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50 data-[disabled]:text-fbui-text-disabled'

/** Per-status border + focus ring (mirrors fieldGroupStatus). */
const dateInputStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
} as const

type FieldStatus = keyof typeof dateInputStatus

export interface DateInputProps {
  size?: FieldSize
  status?: FieldStatus
  className?: string
}

/**
 * Styled segment container. Renders a `DateSegment` for each part automatically
 * — no render-prop boilerplate needed. Reused by `TimeField` and `DatePicker`.
 */
export const DateInput = ({
  size = 'md',
  status = 'default',
  className,
}: DateInputProps) => (
  <AriaDateInput
    className={cn(
      dateInputBase,
      inputSize[size],
      dateInputStatus[status],
      className
    )}
  >
    {(segment) => <DateSegment segment={segment} />}
  </AriaDateInput>
)
DateInput.displayName = 'DateInput'

/** One editable part of the value (day/month/year/hour/minute/AM-PM). */
export const DateSegment = ({
  segment,
  className,
}: {
  segment: React.ComponentProps<typeof AriaDateSegment>['segment']
  className?: string
}) => (
  <AriaDateSegment
    segment={segment}
    className={cn(
      'rounded px-0.5 text-center tabular-nums outline-none caret-transparent',
      'data-[placeholder]:text-fbui-gray-400',
      // Light selection-style highlight (matches the focus-ring tint) — keeps the
      // digits legible, unlike a solid dark fill.
      'data-[focused]:bg-fbui-basil-100 data-[focused]:text-fbui-basil-700',
      'data-[disabled]:text-fbui-text-disabled',
      'data-[type=literal]:px-0 data-[type=literal]:text-fbui-gray-400',
      className
    )}
  />
)
DateSegment.displayName = 'DateSegment'

export interface DateFieldProps extends Omit<
  AriaDateFieldProps<DateValue>,
  'isDisabled' | 'isReadOnly' | 'isRequired' | 'isInvalid' | 'className'
> {
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  className?: string
}

export const DateField = React.forwardRef<HTMLDivElement, DateFieldProps>(
  (
    {
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      className,
      ...props
    },
    ref
  ) => (
    <AriaDateField
      ref={ref}
      isDisabled={disabled}
      isReadOnly={readOnly}
      isRequired={required}
      isInvalid={invalid}
      className={cn('flex flex-col gap-1.5', className)}
      {...props}
    >
      <DateInput size={size} status={invalid ? 'error' : status} />
    </AriaDateField>
  )
)
DateField.displayName = 'DateField'
